import React from 'react'
import { Link } from 'react-router-dom'
import { AuthForm } from '../../../../components/AuthForm/AuthForm'
import { LoadingForm } from '../../../../components/LoadingForm/LoadingForm'
import { Title } from '../../../../components/Title/Title'
import { ROUTES } from '../../../../constants/routes'
import { useAuth } from '../../../../hooks/useAuth'
import { useFetchUserMembers } from '../../../../hooks/useFetchUserMember'
import { useRouter } from '../../../../hooks/useRouter'
import { toQueryParams } from '../../../../utils/query-params'
import { AcceptInviteForm } from './components/AcceptInviteForm'
import { useFetchInvite } from './hooks/useFetchInvite'

export const AcceptInvitePage = (): React.ReactElement => {
  const { user, isLoading: isLoadingUser } = useAuth()
  const { data: members, isLoading: isLoadingMembers } = useFetchUserMembers({
    enabled: !!user && !isLoadingUser,
    redirect: false,
  })
  const router = useRouter()
  const code: string | undefined = router.search.code?.toString()
  const {
    data: invite,
    isLoading,
    isError,
    error,
    handleAccept,
  } = useFetchInvite({
    code,
  })
  const params = toQueryParams({ goto: router.location.pathname + router.location.search })
  const search = params ? `?${params}` : undefined

  if (isLoading || isLoadingMembers) {
    return (
      <AuthForm
        useProgress={false}
        error={error?.message}
        cardFooter={
          !user ? (
            <span className="d-block w-100 text-center">
              <Link to={{ pathname: ROUTES.LOGIN, search }} className="ms-2">
                Have an account? Login
              </Link>
            </span>
          ) : (
            <div></div>
          )
        }
      >
        <Title value="Loading..." />
        <LoadingForm />
      </AuthForm>
    )
  }

  if (isError || !code || !invite || invite?.accepted) {
    return (
      <AuthForm
        useProgress={false}
        error={error?.message}
        cardFooter={
          !user ? (
            <span className="d-block w-100 text-center">
              <Link to={{ pathname: ROUTES.LOGIN, search }} className="ms-2">
                Have an account? Login
              </Link>
            </span>
          ) : (
            <div></div>
          )
        }
      >
        <Title value="Invalid Invite" />
        <h4>Invalid Invite</h4>
        <p>This invite has either been used, expired, or is invalid.</p>
        <Link to={ROUTES.LOGIN} className="d-block">
          Return to login
        </Link>
      </AuthForm>
    )
  }

  const isAlreadyMember =
    !!members?.some((member) => member.program.id === invite?.program.id) && members.length > 0
  if (!!user && isAlreadyMember) {
    const url = new URL(window.location.origin)
    url.pathname = ROUTES.LOGOUT
    url.searchParams.append('goto', window.location.pathname + window.location.search)
    return (
      <AuthForm useProgress={false} error={error?.message}>
        <Title value="You are already team member" />
        <h4>You are already team member</h4>
        <p>
          You are already a team member of {invite.program.name}. Please log out of this account or
          continue onto your dashboard.
        </p>
        <p className="text-center">
          <Link to={ROUTES.DASHBOARD} className="btn btn-primary btn-block d-md-inline w-md-auto">
            Take me to my dashboard
          </Link>
        </p>
        <p className="text-center">
          <Link to={url.toString()} className="btn btn-link btn-block d-md-inline w-md-auto">
            Not {user?.first_name}? Log out
          </Link>
        </p>
      </AuthForm>
    )
  }

  return (
    <AuthForm
      useProgress={false}
      error={error?.message}
      cardFooter={
        !user ? (
          <span className="d-block w-100 text-center">
            <Link to={{ pathname: ROUTES.LOGIN, search }} className="ms-2">
              Have an account? Login
            </Link>
          </span>
        ) : (
          <div></div>
        )
      }
    >
      <Title value="Accept Invite" />
      <AcceptInviteForm code={code} user={user} invite={invite} onSubmit={handleAccept} />
    </AuthForm>
  )
}
